import React from 'react';
import {
  Drawer,
  Box,
  IconButton,
  Button,
  FormControlLabel,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { fetchUserAttributes, fetchAuthSession } from 'aws-amplify/auth';
import { scheduleFilterButtonColors } from '../../utils/colors';
import CustomSwitch from '../CustomSwitch';



const MemoFilterSidebar = ({
  isMobile,
  isDrawerOpen,
  onDrawerClose,
  user,
  isDesk,
  onSignOut,
  autoUpdate,
  setAutoUpdate,
  // NewsGenre
  newsGenreOptions,
  selectedNewsGenres,
  setSelectedNewsGenres,
  // MediaStatus
  mediaStatusOptions,
  selectedMediaStatuses,
  setSelectedMediaStatuses,
  // PublishingStatus
  publishingStatusOptions,
  selectedPublishingStatuses,
  setSelectedPublishingStatuses,
}) => {
  const [cognitoName, setCognitoName] = React.useState('');
  const [groupLabel, setGroupLabel] = React.useState(isDesk ? 'デスク' : 'ユーザー');

  React.useEffect(() => {
    let mounted = true;

    const mapGroupsToLabel = (groups) => {
      const arr = Array.isArray(groups) ? groups : (typeof groups === 'string' ? [groups] : []);
      const lower = arr.map(g => String(g).toLowerCase());
      if (lower.some(g => g === 'admins' || g.includes('admin'))) return '管理者';
      if (lower.some(g => g === 'desks' || g === 'desk' || g.includes('desk'))) return 'デスク';
      if (lower.some(g => g === 'reporters' || g === 'reporter' || g.includes('reporter'))) return '記者';
      if (lower.some(g => g === 'users' || g === 'user')) return 'ユーザー';
      return 'ユーザー';
    };

    (async () => {
      try {
        const attrs = await fetchUserAttributes().catch(() => null);
        if (mounted && attrs) {
          const display =
            attrs?.name ||
            ([attrs?.family_name, attrs?.given_name].filter(Boolean).join(' ') || '') ||
            attrs?.preferred_username ||
            '';
          setCognitoName(display);
        }
      } catch (e) {
        console.warn('ユーザー属性取得失敗', e);
      }

      try {
        const session = await fetchAuthSession();
        const groups =
          session?.tokens?.idToken?.payload?.['cognito:groups'] ??
          session?.idToken?.payload?.['cognito:groups'] ??
          [];
        if (mounted) {
          setGroupLabel(mapGroupsToLabel(groups));
        }
      } catch (e) {
        if (mounted) setGroupLabel(isDesk ? 'デスク' : 'ユーザー');
        console.warn('グループ情報取得失敗', e);
      }
    })();

    return () => { mounted = false; };
  }, [isDesk]);

  const toggleFilter = (current, setCurrent, value) => {
    if (current.includes(value)) {
      setCurrent(current.filter((v) => v !== value));
    } else {
      setCurrent([...current, value]);
    }
  };

  const filterButtonSx = (isSelected) => ({
    textTransform: 'none',
    fontWeight: 'bold',
    backgroundColor: isSelected
      ? scheduleFilterButtonColors.background
      : scheduleFilterButtonColors.selectedBackground,
    color: isSelected
      ? scheduleFilterButtonColors.text
      : scheduleFilterButtonColors.selectedText,
    borderColor: scheduleFilterButtonColors.border,
    borderWidth: '1px',
    borderStyle: 'solid',
    transition: 'background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease',
    '&:hover': {
      backgroundColor: scheduleFilterButtonColors.hoverBackground,
      borderColor: scheduleFilterButtonColors.hoverBorder,
    },
    minWidth: 'auto',
  });

  const renderFilterSection = (label, options, selected, setSelected) => (
    <Box sx={{ mb: 3 }}>
      {label && (
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 'bold', mb: 1, color: '#333', textAlign: 'center' }}
        >
          {label}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
        {options.map((option) => (
          <Button
            key={option}
            variant={selected.includes(option) ? 'contained' : 'outlined'}
            onClick={() => toggleFilter(selected, setSelected, option)}
            sx={filterButtonSx(selected.includes(option))}
          >
            {option}
          </Button>
        ))}
      </Box>
    </Box>
  );

  return (
    <Drawer
      anchor="right"
      open={!isMobile || isDrawerOpen}
      onClose={onDrawerClose}
      variant={!isMobile ? 'persistent' : 'temporary'}
      sx={{
        '& .MuiDrawer-paper': {
          width: 300,
          bgcolor: '#f5f5f5',
          boxShadow: 3,
          p: 2,
          position: !isMobile ? 'relative' : 'fixed',
        },
      }}
    >
      {isMobile && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            mt: 0,
            mb: 2,
          }}
        >
          <IconButton onClick={onDrawerClose} aria-label="close" sx={{ right: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      {/* ユーザーステータス */}
      <Box sx={{ mt: 1, mb: 2, textAlign: 'center', width: '100%' }}>
        <Box
          sx={{
            display: 'inline-block',
            px: 4,
            py: 1,
            bgcolor: '#e0e0e0',
            borderRadius: 2,
            border: '1px solid #bdbdbd',
            boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px',
          }}
        >
          <Typography variant="body2" component="div">
            <Box sx={{ mt: 1, mb: 1, textAlign: 'left' }}>
              ユーザー：{cognitoName ? `${cognitoName}` : ''}
            </Box>
            <Box sx={{ mt: 1, mb: 1, textAlign: 'left' }}>
              グループ：{groupLabel}
            </Box>
          </Typography>
        </Box>
      </Box>

      {/* 自動更新 */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          mt: isMobile ? 1 : 3,
          mb: isMobile ? 1 : 3,
        }}
      >
        <FormControlLabel
          control={
            <CustomSwitch
              checked={autoUpdate}
              onChange={(e) => setAutoUpdate(e.target.checked)}
            />
          }
          label="自動更新"
          sx={{
            gap: 1.2,
            '& .MuiFormControlLabel-label': {
              fontSize: '1.0rem',
              fontWeight: 'bold',
              color: autoUpdate ? '#000000' : '#a9a9a9',
              transition: 'color 0.5s ease-in-out',
            },
          }}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          mt: 3,
          mb: 3,
        }}
      >
        {/* メディア状態 */}
        {renderFilterSection(
          '',
          mediaStatusOptions,
          selectedMediaStatuses,
          setSelectedMediaStatuses
        )}

        {/* 掲載状態 */}
        {renderFilterSection(
          '',
          publishingStatusOptions,
          selectedPublishingStatuses,
          setSelectedPublishingStatuses
        )}

        {/* ニュースジャンル */}
        {renderFilterSection(
          '',
          newsGenreOptions,
          selectedNewsGenres,
          setSelectedNewsGenres
        )}
      </Box>

      {/* 全て選択 / 全て解除 */}
      <Box sx={{ display: 'flex', gap: 1, mt: 3, mb: 3, justifyContent: 'center', width: '100%' }}>
        <Button
          variant="outlined"
          onClick={() => {
            setSelectedNewsGenres([...newsGenreOptions]);
            setSelectedMediaStatuses([...mediaStatusOptions]);
            setSelectedPublishingStatuses([...publishingStatusOptions]);
          }}
          sx={{
            textTransform: 'none',
            fontWeight: 'bold',
            backgroundColor: scheduleFilterButtonColors.background,
            color: scheduleFilterButtonColors.text,
            borderColor: scheduleFilterButtonColors.border,
            transition: 'background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease',
            '&:hover': {
              backgroundColor: scheduleFilterButtonColors.hoverBackground,
              color: scheduleFilterButtonColors.text,
              borderColor: scheduleFilterButtonColors.hoverBorder,
            },
          }}
        >
          全て選択
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            setSelectedNewsGenres([]);
            setSelectedMediaStatuses([]);
            setSelectedPublishingStatuses([]);
          }}
          sx={{
            textTransform: 'none',
            fontWeight: 'bold',
            backgroundColor: scheduleFilterButtonColors.background,
            color: scheduleFilterButtonColors.text,
            borderColor: scheduleFilterButtonColors.border,
            transition: 'background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease',
            '&:hover': {
              backgroundColor: scheduleFilterButtonColors.hoverBackground,
              color: scheduleFilterButtonColors.text,
              borderColor: scheduleFilterButtonColors.hoverBorder,
            },
          }}
        >
          全て解除
        </Button>
      </Box>

      {/* ログアウト */}
      <Box sx={{ mt: 5, textAlign: 'center', width: '100%' }}>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => {
            if (window.confirm('ログアウトしますか？')) {
              onSignOut();
            }
          }}
          size="small"
          sx={{
            display: 'inline-block',
            bgcolor: '#212121',
            color: '#fff',
            fontWeight: 'bold',
            letterSpacing: '0.05em',
            '&:hover': { bgcolor: '#424242' },
            mt: 2,
            mb: 2,
            px: 3,
            py: 1,
          }}
        >
          ログアウト
        </Button>
      </Box>
    </Drawer>
  );
};

export default MemoFilterSidebar;
